import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { RedditAuthProvider } from './pages/reddit/RedditAuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import CampaignViewerRedirect from './components/CampaignViewerRedirect';
import RedditProtectedRoute from './pages/reddit/RedditProtectedRoute';
import RedditLoginPage from './pages/reddit/RedditLoginPage';
import ShareableReport from './pages/reddit/ShareableReport';
import ForgotPasswordPage from './pages/reddit/ForgotPasswordPage';
import ResetPasswordPage from './pages/reddit/ResetPasswordPage';
import RedditAcceptInvitePage from './pages/reddit/AcceptInvitePage';
import RedditLayout from './pages/reddit/RedditLayout';
import Home from './pages/Home';
import Status from './pages/Status';
import InitialOnboardingDataApproval from './pages/forms/InitialOnboardingDataApproval';
import PromptsFeedback from './pages/forms/PromptsFeedback';
import RedditCommentsApproval from './pages/forms/RedditCommentsApproval';
import ContentStrategyReview from './pages/forms/ContentStrategyReview';
import ClientOnboarding from './pages/forms/ClientOnboarding';
import AiVisLogin from './pages/ai-vis/Login';
import AcceptInvitePage from './pages/ai-vis/AcceptInvitePage';
import AiVisForgotPasswordPage from './pages/ai-vis/ForgotPasswordPage';
import AiVisResetPasswordPage from './pages/ai-vis/ResetPasswordPage';
import AiVisLayout from './pages/ai-vis/AiVisLayout';
import AiVisDashboardPage from './pages/ai-vis/DashboardPage';
import CampaignWizardPage from './pages/ai-vis/CampaignWizardPage';
import AdminConsolePage from './pages/ai-vis/AdminConsolePage';
import BrandsPage from './pages/ai-vis/BrandsPage';
import CompetitorsPage from './pages/ai-vis/CompetitorsPage';
import FanOutQueriesPage from './pages/ai-vis/FanOutQueriesPage';
import ResponsesPage from './pages/ai-vis/ResponsesPage';
import PromptsPage from './pages/ai-vis/PromptsPage';
import PromptDetailPage from './pages/ai-vis/PromptDetailPage';
import DomainsPage from './pages/ai-vis/DomainsPage';
import DomainDetailPage from './pages/ai-vis/DomainDetailPage';
import ContentStrategyPage from './pages/ai-vis/ContentStrategyPage';
import SourcesPage from './pages/ai-vis/SourcesPage';
import SourceDetailPage from './pages/ai-vis/SourceDetailPage';
import SettingsPage from './pages/ai-vis/SettingsPage';
import DataStudioDocsPage from './pages/ai-vis/DataStudioDocsPage';
import SystemWideOverviewPage from './pages/ai-vis/SystemWideOverviewPage';
import EnrichmentTrackingPage from './pages/ai-vis/EnrichmentTrackingPage';
import CampaignOverviewPage from './pages/ai-vis/CampaignOverviewPage';
import NotFoundPage from './pages/NotFoundPage';
import './styles/global.css';

const AI_VIS_TITLE = 'AI-Vis | 180Marketing';
const DEFAULT_TITLE = '180Marketing';

function DocumentTitle() {
  const { pathname } = useLocation();

  useEffect(() => {
    const onAiVis = pathname === '/ai-vis' || pathname.startsWith('/ai-vis/');
    if (onAiVis) {
      document.title = AI_VIS_TITLE;
      return;
    }
    if (document.title === AI_VIS_TITLE) {
      document.title = DEFAULT_TITLE;
    }
  }, [pathname]);

  return <Outlet />;
}

const router = createBrowserRouter([
  {
    element: <DocumentTitle />,
    children: [
  { path: '/', element: <Home /> },
  { path: '/status', element: <Status /> },
  { path: '/forms/initial-onboarding-data-approval/:uuid', element: <InitialOnboardingDataApproval /> },
  { path: '/forms/prompts-feedback/:uuid', element: <PromptsFeedback /> },
  { path: '/forms/reddit-comments-approval/:uuid', element: <RedditCommentsApproval /> },
  { path: '/forms/content-strategy-review/:uuid', element: <ContentStrategyReview /> },
  { path: '/forms/client-onboarding', element: <ClientOnboarding /> },
  { path: '/docs/ai-vis/looker-studio', element: <DataStudioDocsPage /> },
  { path: '/ai-vis/login', element: <AiVisLogin /> },
  { path: '/ai-vis/accept-invite', element: <AcceptInvitePage /> },
  { path: '/ai-vis/forgot-password', element: <AiVisForgotPasswordPage /> },
  { path: '/ai-vis/reset-password', element: <AiVisResetPasswordPage /> },
  {
    path: '/ai-vis',
    element: (
      <ProtectedRoute>
        <AiVisLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="home" replace /> },
      {
        path: 'overview',
        element: (
          <CampaignViewerRedirect>
            <CampaignOverviewPage />
          </CampaignViewerRedirect>
        ),
      },
      { path: 'home', element: <AiVisDashboardPage /> },
      { path: 'dashboard', element: <Navigate to="/ai-vis/home" replace /> },
      { path: 'campaigns/new', element: <CampaignWizardPage /> },
      { path: 'prompts/:promptId', element: <PromptDetailPage /> },
      { path: 'prompts', element: <PromptsPage /> },
      { path: 'responses', element: <ResponsesPage /> },
      { path: 'competitors', element: <CompetitorsPage /> },
      { path: 'fan-out-queries', element: <FanOutQueriesPage /> },
      { path: 'domains', element: <DomainsPage /> },
      { path: 'domains/:domain', element: <DomainDetailPage /> },
      { path: 'content-strategy', element: <ContentStrategyPage /> },
      { path: 'sources', element: <SourcesPage /> },
      { path: 'sources/detail', element: <SourceDetailPage /> },
      { path: 'brands', element: <BrandsPage /> },
      { path: 'settings', element: <SettingsPage /> },
      {
        path: 'members',
        element: (
          <AdminRoute>
            <AdminConsolePage />
          </AdminRoute>
        ),
      },
      { path: 'admin', element: <Navigate to="/ai-vis/members" replace /> },
      { path: 'system-wide-overview', element: <SystemWideOverviewPage /> },
      { path: 'enrichment-tracking', element: <EnrichmentTrackingPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  // Reddit Campaign Hub — standalone app with its own auth
      { path: '/reddit/login', element: <RedditLoginPage /> },
      { path: '/reddit/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reddit/reset-password', element: <ResetPasswordPage /> },
      { path: '/reddit/accept-invite', element: <RedditAcceptInvitePage /> },
  {
    path: '/reddit',
    element: (
      <RedditProtectedRoute>
        <RedditLayout />
      </RedditProtectedRoute>
    ),
  },
  {
    path: '/reddit/dashboard',
    element: (
      <RedditProtectedRoute>
        <RedditLayout />
      </RedditProtectedRoute>
    ),
  },
  {
    path: '/reddit/admin/redditors/:redditorId',
    element: (
      <RedditProtectedRoute adminOnly>
        <RedditLayout />
      </RedditProtectedRoute>
    ),
  },
  {
    // Public shareable report — no auth required
    path: '/reddit/report/:token',
    element: <ShareableReport />,
  },
  { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

function App() {
  return (
    <AuthProvider>
      <RedditAuthProvider>
        <RouterProvider router={router} />
      </RedditAuthProvider>
    </AuthProvider>
  );
}

export default App;
